import { useEffect, useRef, useState, ChangeEvent } from 'react';
import { uploadLogo, removeLogo, getSignedUrl } from '../lib/storage';
import { updateOrgLogo } from '../lib/db';
import { useConfirm } from './ConfirmProvider';

interface Props {
  orgId: string;
  currentLogoPath: string | null;
  onChange: () => void;
}

export function LogoUpload({ orgId, currentLogoPath, onChange }: Props) {
  const { confirm } = useConfirm();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    if (currentLogoPath) {
      getSignedUrl('org-logos', currentLogoPath, 3600)
        .then((url) => { if (!cancelled) setPreviewUrl(url); })
        .catch(() => { if (!cancelled) setPreviewUrl(null); });
    } else {
      setPreviewUrl(null);
    }
    return () => { cancelled = true; };
  }, [currentLogoPath]);

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg|gif)$/i.test(file.type)) {
      setError('Please upload a PNG or JPEG image.');
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image is over 5MB. Please use something smaller.');
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const path = await uploadLogo(orgId, file);
      await updateOrgLogo(orgId, path);
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function onRemove() {
    if (!currentLogoPath) return;
    if (!(await confirm({
      title: 'Remove your logo?',
      body: 'PDFs will fall back to the org name only. You can upload a new logo at any time.',
      confirmLabel: 'Remove logo',
      danger: true,
    }))) return;
    setUploading(true);
    setError(null);
    try {
      await removeLogo(orgId, currentLogoPath);
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="logo-upload">
      <div className="logo-preview-frame">
        {previewUrl ? (
          <img src={previewUrl} alt="Your logo" className="logo-preview-img" />
        ) : (
          <span className="logo-preview-empty">No logo yet</span>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/png, image/jpeg"
        hidden
        onChange={onFile}
      />

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          style={{ width: 'auto', padding: '10px 16px' }}
        >
          {uploading ? 'Uploading…' : currentLogoPath ? 'Replace logo' : 'Upload logo'}
        </button>
        {currentLogoPath && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onRemove}
            disabled={uploading}
            style={{ width: 'auto', padding: '10px 16px', color: 'var(--danger)', borderColor: 'var(--danger)' }}
          >
            Remove
          </button>
        )}
      </div>

      {error && (
        <div className="banner banner-error" style={{ marginTop: 12 }}>{error}</div>
      )}

      <p className="muted" style={{ marginTop: 12 }}>
        PNG or JPEG, ideally with a transparent background. Appears on PDF
        exports and on the home page. Max 5MB.
      </p>
    </div>
  );
}
